/**
 * Gateways store — Context + provider for the tenant's edge appliances.
 *
 * Backs the human-facing Devices table + the Add Gateway flow (both
 * landing in B0.45). Mirrors the DashboardsProvider / AlarmsProvider
 * pattern: a global React Context, in-memory state, localStorage
 * persistence (`divineiot:gateways`), and a `version` counter so memoised
 * consumers re-render on mutation.
 *
 * Phase 1 swaps storage for `/v1/iot/gateways` CRUD + the real
 * `POST /v1/iot/gateways/{gw_id}/claim` endpoint (already shipped on
 * Arun's side — see api/openapi.yaml).
 *
 * Seed: the current `FX_DEVICES` array from `lib/widgets/__fixtures.ts`
 * so the Devices table loads with the same 8 fixture gateways the demo
 * always had.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { FX_DEVICES, type DemoDeviceRow } from "@/lib/widgets/__fixtures";
import { useThingsQuery, toThingRecord, backendErrorMessage, isLiveApi } from "@/lib/api";
import { useToast } from "@/lib/ui/toast";
import { makeClaimCode } from "./make-claim-code";

const STORAGE_KEY = "divineiot:gateways";

export type GatewayProtocol = DemoDeviceRow["protocol"];

export interface GatewayRecord extends DemoDeviceRow {
  /**
   * The gateway product this Thing instantiates (P6.6). When present, the
   * Things page resolves the displayed *field protocol* from this against
   * the products catalogue (the `protocol` string is then a render-time
   * label, not a stored truth). Null for fixture rows.
   */
  productId?: string | null;
  /** Per-Thing simulator toggle (P6 / P-wave3) — surfaced in the detail sheet's edit mode. */
  simulate?: boolean;
  /**
   * Factory/demo Thing flag (migration 0033). Drives the "Demo" badge + the
   * default-on "Hide demo" filter on the Things page so the demo plane is
   * visibly separate from real tenant-added Things.
   */
  isSimulated?: boolean;
  /**
   * Display name of the vendor that supplied this Thing (things.vendor_org_id →
   * orgs.name). Drives a vendor badge (e.g. "Versatile") on vendor-assigned
   * equipment in the customer's device views. Absent for non-vendor gear.
   */
  vendorName?: string;
  /** One-time claim code issued at create / regenerate. */
  claimCode?: string;
  /** ISO timestamp when the claim code expires (server-issued ~1h; advisory client-side). */
  claimCodeExpiresAt?: string;
  /**
   * The Thing's stable slug — the on-wire identity: it is the `device_id` a
   * gateway publishes in its envelope and the key readings are stored under.
   * Operators need it to configure/debug a device (e.g. the VSync onboarding).
   */
  slug?: string;
}

export interface GatewaysState {
  gateways: GatewayRecord[];
  byId: (id: string) => GatewayRecord | undefined;

  /**
   * Create a new gateway. Returns the new id + the freshly minted claim
   * code (one-time, ~24h advisory expiry).
   */
  create: (input: {
    name: string;
    site: string;
    protocol: GatewayProtocol;
    twinEntityId?: string | null;
    twinEntityName?: string | null;
  }) => { id: string; claimCode: string };

  /**
   * Regenerate a claim code — **stub / no-backend mode only** (fabricates a
   * local code). In live mode the caller mints via `POST /v1/things/{id}/claim`
   * and stores the server-issued code with {@link setClaimCode}.
   */
  regenerateClaimCode: (id: string) => string;

  /**
   * Store a real server-issued claim code (`POST /v1/things/{id}/claim`) on a
   * gateway record — the live-mode counterpart to {@link regenerateClaimCode}.
   */
  setClaimCode: (id: string, claimCode: string, expiresAt: string) => void;

  /** Patch a gateway. */
  update: (id: string, patch: Partial<GatewayRecord>) => void;

  /** Remove a gateway. */
  remove: (id: string) => void;

  /** Reset to fixture seed. */
  reset: () => void;

  /** Mutation counter — drives memoised consumers. */
  version: number;
}

const GatewaysContext = createContext<GatewaysState | null>(null);

export function useGateways(): GatewaysState {
  const ctx = useContext(GatewaysContext);
  if (!ctx) throw new Error("useGateways must be used inside <GatewaysProvider>");
  return ctx;
}

export function useOptionalGateways(): GatewaysState | null {
  return useContext(GatewaysContext);
}

function readPersisted(): GatewayRecord[] | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed as GatewayRecord[];
  } catch {
    return null;
  }
}

function writePersisted(gateways: GatewayRecord[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(gateways));
  } catch {
    /* swallow */
  }
}

/** Convert the static FX_DEVICES seed into the GatewayRecord shape. */
const SEED_GATEWAYS: GatewayRecord[] = FX_DEVICES.map((d) => ({ ...d }));

// Display labels for the known field protocols: the products' field_protocol
// values and the driver registry's ids (modbus-tcp, modbus-rtu, opcua, s7,
// enip, melsec). Unknown ids fall back to the raw value (the products
// catalogue is the live source of truth).
const PROTOCOL_LABEL: Record<string, string> = {
  "modbus-tcp": "Modbus TCP",
  "modbus-rtu": "Modbus RTU",
  opcua: "OPC UA",
  s7: "Siemens S7",
  enip: "EtherNet/IP",
  melsec: "MELSEC",
  mqtt: "MQTT",
  "generic-mqtt": "Generic MQTT",
  "multi-protocol": "Multi-protocol",
  bacnet: "BACnet",
};

export function getProtocolLabel(p: GatewayProtocol): string {
  return PROTOCOL_LABEL[p] ?? p;
}

export function GatewaysProvider({ children }: { children: ReactNode }) {
  // Live mode: start empty + let the org-scoped query populate. Fixtures +
  // the global localStorage key are stub-mode only (they leaked one org's
  // gateways into the next).
  const [gateways, setGateways] = useState<GatewayRecord[]>(
    () => (isLiveApi ? [] : (readPersisted() ?? SEED_GATEWAYS)),
  );
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!isLiveApi) writePersisted(gateways);
  }, [gateways]);

  const bumpVersion = useCallback(() => setVersion((v) => v + 1), []);

  // ── B1.0 live-API wiring ──────────────────────────────────────────
  // When VITE_USE_LIVE_API=true, useThingsQuery fetches the tenant's
  // things (P2.2 — gateways + sensor children + standalone); on data, we
  // replace the fixture/localStorage seed with the mapped server rows. With
  // the flag unset the query is disabled and this provider behaves exactly
  // as before (fixtures only).
  //
  // NOTE: a refetch CLOBBERS local create/update/remove mutations, which
  // are still local-only in B1.0. B1.1 adds a `hasHydrated` guard +
  // wraps mutations in useMutation with optimistic updates so the replace
  // becomes first-load-only. See docs/backlog/b1.0-react-query-and-typed-resources.md.
  const query = useThingsQuery();
  const toast = useToast();
  const erroredRef = useRef(false);

  useEffect(() => {
    if (query.data) {
      setGateways(query.data.items.map(toThingRecord));
      bumpVersion();
    }
  }, [query.data, bumpVersion]);

  // Only a first read that failed: a failed re-read (the Gateways page reads again every 30 s, and a deploy restarts
  // the API) keeps the list on screen, and its "Updated ... ago" shows its age.
  useEffect(() => {
    if (query.error && !query.data && !erroredRef.current) {
      erroredRef.current = true;
      toast.push("error", backendErrorMessage(query.error));
    }
  }, [query.error, query.data, toast]);
  // ──────────────────────────────────────────────────────────────────

  const byId = useCallback(
    (id: string) => gateways.find((g) => g.id === id),
    [gateways],
  );

  const create = useCallback<GatewaysState["create"]>(
    (input) => {
      const id = `gw-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      const claimCode = makeClaimCode();
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      const nowIso = new Date().toISOString();
      const certExpires = new Date(
        Date.now() + 365 * 24 * 60 * 60 * 1000,
      ).toISOString();

      const next: GatewayRecord = {
        id,
        name: input.name,
        site: input.site,
        protocol: input.protocol,
        tags: 0,
        msgPerSec: 0,
        msgPerSecHistory: Array.from({ length: 20 }, (_, i) => ({
          t: new Date(Date.now() - (19 - i) * 60_000).toISOString(),
          v: 0,
        })),
        lastSeen: nowIso,
        status: "unknown",
        certExpiresAt: certExpires,
        twinEntityId: input.twinEntityId ?? null,
        twinEntityName: input.twinEntityName ?? null,
        claimCode,
        claimCodeExpiresAt: expiresAt,
      };

      setGateways((prev) => [next, ...prev]);
      bumpVersion();
      return { id, claimCode };
    },
    [bumpVersion],
  );

  const regenerateClaimCode = useCallback<GatewaysState["regenerateClaimCode"]>(
    (id) => {
      const claimCode = makeClaimCode();
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      setGateways((prev) =>
        prev.map((g) =>
          g.id === id ? { ...g, claimCode, claimCodeExpiresAt: expiresAt } : g,
        ),
      );
      bumpVersion();
      return claimCode;
    },
    [bumpVersion],
  );

  const setClaimCode = useCallback<GatewaysState["setClaimCode"]>(
    (id, claimCode, expiresAt) => {
      setGateways((prev) =>
        prev.map((g) =>
          g.id === id ? { ...g, claimCode, claimCodeExpiresAt: expiresAt } : g,
        ),
      );
      bumpVersion();
    },
    [bumpVersion],
  );

  const update = useCallback<GatewaysState["update"]>(
    (id, patch) => {
      setGateways((prev) =>
        prev.map((g) => (g.id === id ? { ...g, ...patch, id } : g)),
      );
      bumpVersion();
    },
    [bumpVersion],
  );

  const remove = useCallback<GatewaysState["remove"]>(
    (id) => {
      setGateways((prev) => prev.filter((g) => g.id !== id));
      bumpVersion();
    },
    [bumpVersion],
  );

  const reset = useCallback(() => {
    setGateways(SEED_GATEWAYS);
    bumpVersion();
  }, [bumpVersion]);

  const value = useMemo<GatewaysState>(
    () => ({
      gateways,
      byId,
      create,
      regenerateClaimCode,
      setClaimCode,
      update,
      remove,
      reset,
      version,
    }),
    [gateways, byId, create, regenerateClaimCode, setClaimCode, update, remove, reset, version],
  );

  return (
    <GatewaysContext.Provider value={value}>
      {children}
    </GatewaysContext.Provider>
  );
}
